import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Activity, Users, Clock, RefreshCw } from 'lucide-react';
import { logger } from '../services/logger.ts';
import { supabase } from '../lib/db';
import { ClickableUserName } from './common/ClickableUserName';

interface MonitoringStats {
  securityAlerts: number;
  failedLogins: number;
  totalRequests: number;
  uniqueUsers: number;
}

interface FailedLoginStat {
  email: string;
  count: number;
  user_id?: string;
  user_name?: string;
}

interface TrafficStat {
  route: string;
  count: number;
}

interface SecurityEvent {
  id: string;
  event_type: string;
  event_data: Record<string, unknown>;
  severity: string;
  event_timestamp: string;
}

interface FailedLoginRecord {
  id: string;
  email: string;
  login_timestamp: string;
  ip?: string;
  user_agent?: string;
  reason?: string;
  error_code?: string;
  error_type?: string;
  error_details?: string;
}

export function MonitoringDashboard() {
  const [stats, setStats] = useState<MonitoringStats>({
    securityAlerts: 0,
    failedLogins: 0,
    totalRequests: 0,
    uniqueUsers: 0,
  });
  
  const [failedLoginStats, setFailedLoginStats] = useState<FailedLoginStat[]>([]);
  const [trafficStats, setTrafficStats] = useState<TrafficStat[]>([]);
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [recentFailedLogins, setRecentFailedLogins] = useState<FailedLoginRecord[]>([]);
  const [timeWindow, setTimeWindow] = useState<number>(60); // minutes
  const [refreshInterval, setRefreshInterval] = useState<number>(30); // seconds
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchMonitoringData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel for better performance
      const [
        failedLoginStatsData,
        trafficStatsData,
        securityEventsData,
        recentFailedLoginsData,
      ] = await Promise.all([
        logger.getFailedLoginStats(timeWindow),
        logger.getTrafficStats(timeWindow),
        logger.getSecurityEvents(timeWindow),
        logger.getRecentFailedLogins(timeWindow),
      ]);

      // Enrich failed login stats with user IDs and names
      const enrichedFailedLoginStats = await Promise.all(
        failedLoginStatsData.map(async (stat) => {
          const { data: userData } = await supabase
            .from('users')
            .select('id, user_metadata')
            .eq('email', stat.email)
            .maybeSingle();

          if (userData) {
            const firstName = userData.user_metadata?.first_name || userData.user_metadata?.firstName || '';
            const lastName = userData.user_metadata?.last_name || userData.user_metadata?.lastName || '';
            const userName = [firstName, lastName].filter(Boolean).join(' ').trim() || stat.email.split('@')[0];
            
            return {
              ...stat,
              user_id: userData.id,
              user_name: userName,
            };
          }
          
          return stat;
        })
      );

      // Calculate stats
      const failedLoginCount = failedLoginStatsData.reduce((sum, stat) => sum + stat.count, 0);
      const totalRequestCount = trafficStatsData.reduce((sum, stat) => sum + stat.count, 0);
      const securityAlertCount = securityEventsData.length;
      
      // Estimate unique users from failed logins (this is a simplified approach)
      const uniqueUserCount = new Set(recentFailedLoginsData.map(login => login.email)).size;

      setStats({
        securityAlerts: securityAlertCount,
        failedLogins: failedLoginCount,
        totalRequests: totalRequestCount,
        uniqueUsers: uniqueUserCount,
      });

      setFailedLoginStats(enrichedFailedLoginStats);
      setTrafficStats(trafficStatsData);
      setSecurityEvents(securityEventsData);
      setRecentFailedLogins(recentFailedLoginsData);
      setLastRefresh(new Date());
      
    } catch (error) {
      console.error('Error fetching monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonitoringData();
  }, [timeWindow]);

  useEffect(() => {
    const interval = setInterval(fetchMonitoringData, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [refreshInterval, timeWindow]);

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getTimeWindowLabel = (minutes: number) => {
    if (minutes < 60) return `${minutes} minutes`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)} hours`;
    return `${Math.floor(minutes / 1440)} days`;
  };

  const getRefreshIntervalLabel = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m`;
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Security & Traffic Monitoring</h1>
            <p className="text-gray-600 mt-2">Real-time monitoring of security events and traffic patterns</p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Time Window Selector */}
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <select
                value={timeWindow}
                onChange={(e) => setTimeWindow(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
              >
                <option value={15}>Last 15 minutes</option>
                <option value={30}>Last 30 minutes</option>
                <option value={60}>Last hour</option>
                <option value={180}>Last 3 hours</option>
                <option value={360}>Last 6 hours</option>
                <option value={720}>Last 12 hours</option>
                <option value={1440}>Last 24 hours</option>
              </select>
            </div>

            {/* Refresh Interval Selector */}
            <div className="flex items-center space-x-2">
              <RefreshCw className="w-4 h-4 text-gray-500" />
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
              >
                <option value={10}>Refresh every 10s</option>
                <option value={30}>Refresh every 30s</option>
                <option value={60}>Refresh every 1m</option>
                <option value={300}>Refresh every 5m</option>
              </select>
            </div>

            {/* Manual Refresh Button */}
            <button
              onClick={fetchMonitoringData}
              disabled={loading}
              className="bg-wayja-teal text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Last Refresh Info */}
        <div className="mb-4 text-sm text-gray-500">
          Last updated: {formatTimestamp(lastRefresh.toISOString())} | 
          Showing data from the {getTimeWindowLabel(timeWindow).toLowerCase()} | 
          Auto-refresh: {getRefreshIntervalLabel(refreshInterval)}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Security Alerts</p>
                <p className="text-3xl font-bold text-red-600">{stats.securityAlerts}</p>
              </div>
              <Shield className="w-8 h-8 text-red-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Failed Logins</p>
                <p className="text-3xl font-bold text-orange-600">{stats.failedLogins}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Requests</p>
                <p className="text-3xl font-bold text-blue-600">{stats.totalRequests}</p>
              </div>
              <Activity className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Unique Users</p>
                <p className="text-3xl font-bold text-green-600">{stats.uniqueUsers}</p>
              </div>
              <Users className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>

        {/* Charts and Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Failed Login Attempts */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Failed Login Attempts</h2>
              <span className="text-sm text-gray-500">{getTimeWindowLabel(timeWindow)}</span>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : failedLoginStats.length > 0 ? (
              <div className="space-y-3">
                {failedLoginStats.slice(0, 10).map((stat, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex flex-col">
                      {stat.user_id ? (
                        <>
                          <span className="font-medium text-gray-900">
                            <ClickableUserName userId={stat.user_id} displayName={stat.user_name || stat.email} />
                          </span>
                          <span className="text-xs text-gray-500">{stat.email}</span>
                        </>
                      ) : (
                        <span className="font-medium text-gray-900">{stat.email}</span>
                      )}
                    </div>
                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-sm font-medium">
                      {stat.count} attempts
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                <Shield className="w-12 h-12 text-gray-300 mb-2" />
                <p>No failed login attempts in the selected time window</p>
              </div>
            )}
          </div>

          {/* Traffic by Route */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Traffic by Route</h2>
              <span className="text-sm text-gray-500">Top routes</span>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : trafficStats.length > 0 ? (
              <div className="space-y-3">
                {trafficStats.slice(0, 10).map((stat, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-900">{stat.route}</span>
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                      {stat.count} requests
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                <Activity className="w-12 h-12 text-gray-300 mb-2" />
                <p>No traffic data in the selected time window</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Security Events */}
        <div className="mt-6 bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Security Events</h2>
          
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : securityEvents.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Event Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Severity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {securityEvents.slice(0, 20).map((event) => (
                    <tr key={event.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatTimestamp(event.event_timestamp)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {event.event_type.replace(/_/g, ' ')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          event.severity === 'critical' ? 'bg-red-100 text-red-800' :
                          event.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                          event.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {event.severity}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {JSON.stringify(event.event_data)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500">
              <Shield className="w-12 h-12 text-gray-300 mb-2" />
              <p>No security events in the selected time window</p>
            </div>
          )}
        </div>

        {/* Debug Section - Recent Failed Logins Details */}
        {recentFailedLogins.length > 0 && (
          <div className="mt-6 bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Failed Login Details</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Error Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IP
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentFailedLogins.slice(0, 10).map((login) => (
                    <tr key={login.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatTimestamp(login.login_timestamp)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {login.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {login.reason || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {login.error_type || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {login.ip || 'Unknown'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 